import React from "react";
import { Grid, Typography, Divider } from "@material-ui/core";
import { dateFormatter, addressFormator } from "../../utils/common";
import {
  isInvoiceCancelled,
  formatInvoicePaidDateDisplay,
  getInvoicePaymentHistory,
  getInvoicePaymentMethodLabel,
  shouldShowInvoicePaidDate,
} from "../../utils/invoiceStatusDisplay";
import "./contractInvoiceView.css";

export default function ContractProposal({ data, label, id, inputRef }) {
  const invoiceSource = data?._invoiceRaw || data?._invoiceData || data;
  const showCancelledWatermark =
    Boolean(data?.invoice_no || invoiceSource?.invoice_no) &&
    (isInvoiceCancelled(data) || isInvoiceCancelled(invoiceSource));
  const invoiceRows =
    (Array.isArray(data?.itemDetails) && data.itemDetails.length > 0
      ? data.itemDetails
      : Array.isArray(data?.items) && data.items.length > 0
        ? data.items
        : []) || [];

  const subtotal = parseFloat(data?.sub_total || 0) || 0;
  const total = Math.max(parseFloat(data?.total || 0) || 0, 0);
  const amountPaid = parseFloat(data?.amount_paid || 0) || 0;
  const paymentHistory = (() => {
    const fromSource = getInvoicePaymentHistory(invoiceSource);
    if (fromSource.length) return fromSource;
    return getInvoicePaymentHistory(data);
  })();
  const balanceDue = Math.max(total - amountPaid, 0);
  const referralDiscount = parseFloat(data?.discount_referral_value || 0) || 0;
  const amountDiscount =
    data?.discount_status === 1 && data?.discount_type === "Amount"
      ? parseFloat(data?.discount || 0) || 0
      : 0;
  const percentDiscount =
    data?.discount_status === 1 && data?.discount_type !== "Amount"
      ? (subtotal * (parseFloat(data?.discount || 0) || 0)) / 100
      : 0;
  const discountValue = amountDiscount > 0 ? amountDiscount : percentDiscount;
  const invoiceCategory = String(
    data?.category ??
      data?.invoice_category ??
      invoiceSource?.category ??
      invoiceSource?.invoice_category ??
      "",
  )
    .trim()
    .toLowerCase();
  const isInvoiceView = label === "Invoice" || Boolean(data?.invoice_no);
  const showBusinessEntityName =
    Boolean(data?.bill_to_business_name) &&
    (!isInvoiceView || invoiceCategory.startsWith("business"));

  return (
    <div
      id={id}
      ref={inputRef}
      style={{ padding: 32 }}
      className={`invoice-card-wrapper invoice-print-portrait${
        showCancelledWatermark ? " invoice-card-wrapper--cancelled" : ""
      }`}
    >
      {showCancelledWatermark && (
        <svg
          className="invoice-cancelled-watermark"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 800 1100"
          preserveAspectRatio="xMidYMid meet"
        >
          <text
            x="400"
            y="550"
            textAnchor="middle"
            dominantBaseline="middle"
            transform="rotate(-35 400 550)"
            fill="#D32F2F"
            fillOpacity="0.18"
            fontSize="72"
            fontWeight="700"
            fontFamily="Arial, Helvetica, sans-serif"
            letterSpacing="8"
          >
            CANCELLED
          </text>
        </svg>
      )}
      <Grid
        container
        spacing={0}
        direction="column"
        className="invoice-card invoice-card-content"
      >
      <Grid
        item
        xs={12}
        container
        justifyContent="space-between"
        className="invoice-top-row"
      >
        <Grid
          item
          xs={12}
          md={6}
          className="invoice-left-block"
          // style={{ marginTop: "16px" }}
        >
          <h1 variant="h6" className="invoice-company-name">
            FinloTax Inc
          </h1>
          <Typography variant="body1" className="invoice-text-row">
            1190 Miraloma Way Ste P
          </Typography>
          <Typography variant="body1" className="invoice-text-row">
            Sunnyvale, CA 94085
          </Typography>
          <Typography variant="body1" className="invoice-text-row">
            Email: finance@finlotax.com
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} className="invoice-right-block">
          <img
            src="/resources/images/Finlotax.png"
            alt="logo"
            className="invoice-logo"
          />
          <Typography variant="h5" className="invoice-number">
            INVOICE {data?.invoice_no}
          </Typography>
          <Typography variant="body1" className="invoice-text-row">
            Date: {dateFormatter(data?.invoice_date)}
          </Typography>
          <Typography variant="body1" className="invoice-text-row">
            Due Date: {dateFormatter(data?.due_date)}
          </Typography>
          {paymentHistory.length > 1
            ? paymentHistory.map((row, index) => (
                <Typography
                  key={`${row.payment_date}-${index}`}
                  variant="body1"
                  className="invoice-text-row"
                >
                  Payment {index + 1}:{" "}
                  {formatInvoicePaidDateDisplay({
                    paid_date: row.payment_date,
                  })}{" "}
                  · {getInvoicePaymentMethodLabel(row)} · $
                  {Number(row.amount).toFixed(2)}
                </Typography>
              ))
            : shouldShowInvoicePaidDate(invoiceSource) && (
                <Typography variant="body1" className="invoice-text-row">
                  Paid Date: {formatInvoicePaidDateDisplay(invoiceSource)}
                </Typography>
              )}
        </Grid>
      </Grid>

      <Grid
        item
        xs={12}
        container
        className="invoice-billto-section"
        style={{ marginBottom: "10px" }}
      >
        <Grid item xs={12} md={7}>
          <h1 variant="h6" className="invoice-billto-title">
            Bill To :
          </h1>
          <h1 variant="h6" className="invoice-client-name">
            {data.bill_to_first_name} {data.bill_to_last_name}
          </h1>
          {showBusinessEntityName && (
            <Typography variant="body1" className="invoice-text-row">
              {data.bill_to_business_name}
            </Typography>
          )}
          <Typography variant="body1" className="invoice-text-row">
            {addressFormator(data.bill_to_country)}
          </Typography>
          <Typography variant="body1" className="invoice-text-row">
            {addressFormator(
              data.bill_to_city,
              data.bill_to_state,
              data.bill_to_zipcode,
            )}
          </Typography>
          <Typography variant="body1" className="invoice-text-row">
            {data.bill_to_email}
          </Typography>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider className="invoice-main-divider" />
      </Grid>

      <Grid
        item
        xs={12}
        container
        className="invoice-table-wrapper"
        style={{ marginTop: "10px" }}
      >
        <table className="invoice-table">
          <thead>
            <tr>
              <th align="left">Activity</th>
              <th align="left">Description</th>
              <th align="right">Qty</th>
              <th align="right">Rate</th>
              <th align="right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoiceRows.length > 0 ? (
              invoiceRows.map((row, idx) => {
                const quantity = parseFloat(row.quantity) || 0;
                const amount =
                  parseFloat(row.amount) ||
                  (parseFloat(row.quantity) || 0) * (parseFloat(row.rate) || 0);
                const rate =
                  quantity > 0 ? amount / quantity : parseFloat(row.rate) || 0;

                return (
                  <tr key={row.client_invoice_item_id || row.id || idx}>
                    <td>{row.item_name ?? row.name ?? ""}</td>
                    <td>{row.description ?? ""}</td>
                    <td>{row.quantity ?? ""}</td>
                    <td>{rate.toFixed(2)}</td>
                    <td align="right">{amount.toFixed(2)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td>{data?.client_type}</td>
                <td>{data?.description}</td>
                <td align="right">1</td>
                <td align="right">{subtotal.toFixed(2)}</td>
                <td align="right">{subtotal.toFixed(2)}</td>
              </tr>
            )}

            <tr className="invoice-summary-row">
              <td colSpan={3} />
              <td style={{ paddingTop: 10 }} className="summary-label">
                SUBTOTAL
              </td>
              <td align="right" style={{ paddingTop: 10 }}>
                {subtotal.toFixed(2)}
              </td>
            </tr>
            {referralDiscount > 0 && (
              <tr className="invoice-summary-row">
                <td colSpan={3} />
                <td className="summary-label">REFERRAL DISCOUNT</td>
                <td align="right">{referralDiscount.toFixed(2)}</td>
              </tr>
            )}
            {discountValue > 0 && (
              <tr className="invoice-summary-row">
                <td colSpan={3} />
                <td className="summary-label">DISCOUNT</td>
                <td align="right">{discountValue.toFixed(2)}</td>
              </tr>
            )}
            <tr className="invoice-summary-row invoice-summary-strong">
              <td colSpan={3} />
              <td className="summary-label">TOTAL</td>
              <td align="right">{total.toFixed(2)}</td>
            </tr>
            <tr className="invoice-summary-row">
              <td colSpan={3} />
              <td className="summary-label">PAYMENTS</td>
              <td align="right">-(${amountPaid.toFixed(2)})</td>
            </tr>
            <tr className="invoice-summary-row invoice-summary-strong">
              <td colSpan={3} />
              <td style={{ paddingBottom: 10 }} className="summary-label">
                TOTAL DUE
              </td>
              <td align="right" style={{ paddingBottom: 10 }}>
                $ {balanceDue.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </Grid>

      {String(data?.invoice_comment || "").trim() ? (
        <Grid item xs={12} style={{ marginTop: 16 }}>
          <h1 className="invoice-terms-title">Notes</h1>
          <p style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>
            {String(data.invoice_comment).trim()}
          </p>
        </Grid>
      ) : null}

      <Grid
        item
        xs={12}
        className="invoice-terms-block"
        style={{ marginTop: "20px" }}
      >
        <h1 variant="h4" className="invoice-terms-title">
          Terms and Conditions:
        </h1>
        <ul className="invoice-terms-list" style={{ marginLeft: "10px" }}>
          <li>
            The Client hereby authorizes FinloTax Inc. to provide the services
            and make filings as outlined in this Invoice.
          </li>
          <li>
            The Client confirms that the Fee stated above is accurate and
            agreeable.
          </li>
          <li>
            The Fee shall be paid in full within 3 business days of receipt of
            this Invoice. Failure to pay may result in auto-debit from the
            Client's account.
          </li>
          <li>
            This Agreement shall be governed by in accordance with the laws of
            the State of California, USA. FinloTax Inc.
          </li>
          <li>
            The Client acknowledges that filings/services are based on
            Client-provided information. FinloTax Inc. shall not be liable for
            any statutory department actions or notices.
          </li>
          <li>
            FinloTax Inc. liability shall be limited to the Fees paid in the
            last 2 months. FinloTax Inc. shall not be liable for incidental or
            consequential damages.
          </li>
        </ul>
      </Grid>
      </Grid>
    </div>
  );
}
